import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronLeft,
  RefreshCw,
  ShoppingCart,
  MessageSquare,
  CreditCard,
  BookOpen,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const INTEGRATIONS = [
  { key: 'shopify', name: 'Shopify', icon: ShoppingCart, color: 'green' },
  { key: 'whatsapp', name: 'WhatsApp', icon: MessageSquare, color: 'green' },
  { key: 'mamopay', name: 'MamoPay', icon: CreditCard, color: 'blue' },
  { key: 'zoho', name: 'Zoho Books', icon: BookOpen, color: 'orange' },
];

const SuperAdminIntegrationsPage = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [integrationStatus, setIntegrationStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch companies
      const companiesRes = await axios.get(`${API_BASE_URL}/companies`, { headers });
      const companiesList = companiesRes.data.companies || [];
      setCompanies(companiesList);

      // Fetch integration status for each company
      const statusMap = {};
      for (const company of companiesList) {
        try {
          const integrationsRes = await axios.get(
            `${API_BASE_URL}/companies/${company.id}/integrations`,
            { headers }
          );
          statusMap[company.id] = integrationsRes.data.integrations || {};
        } catch (err) {
          console.error(`Failed to fetch integrations for ${company.name}:`, err);
          statusMap[company.id] = {};
        }
      }
      setIntegrationStatus(statusMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load integration status');
    } finally {
      setLoading(false);
    }
  };

  const refreshIntegration = async (companyId, integrationKey) => {
    const refreshKey = `${companyId}-${integrationKey}`;
    setRefreshing(prev => ({ ...prev, [refreshKey]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Re-fetch integration status for this company
      const integrationsRes = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/integrations`,
        { headers }
      );
      
      setIntegrationStatus(prev => ({
        ...prev,
        [companyId]: integrationsRes.data.integrations || {}
      }));
      
      toast.success('Integration status refreshed');
    } catch (error) {
      console.error('Failed to refresh:', error);
      toast.error('Failed to refresh integration status');
    } finally {
      setRefreshing(prev => ({ ...prev, [refreshKey]: false }));
    }
  };

  const getStatusBadge = (integration) => {
    if (!integration || !integration.configured) {
      return (
        <Badge className="bg-gray-500/10 text-gray-400 border-gray-500/20">
          <XCircle className="h-3 w-3 mr-1" />
          Not Configured
        </Badge>
      );
    }
    
    if (integration.connected || integration.working) {
      return (
        <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
          <CheckCircle className="h-3 w-3 mr-1" />
          Connected
        </Badge>
      );
    }
    
    return (
      <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20">
        <AlertCircle className="h-3 w-3 mr-1" />
        Error
      </Badge>
    );
  };

  const getIntegrationDetails = (companyId, integrationKey) => {
    const companyIntegrations = integrationStatus[companyId] || {};
    return companyIntegrations[integrationKey] || null;
  };

  // Count total integrations
  const getTotalStats = () => {
    let configured = 0;
    let connected = 0;
    let errors = 0;

    companies.forEach(company => {
      const companyIntegrations = integrationStatus[company.id] || {};
      INTEGRATIONS.forEach(integration => {
        const status = companyIntegrations[integration.key];
        if (status?.configured) {
          configured++;
          if (status.connected || status.working) {
            connected++;
          } else {
            errors++;
          }
        }
      });
    });

    return { configured, connected, errors };
  };

  const stats = getTotalStats();

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/super-admin')}
            className="text-wt-text-secondary hover:text-wt-text-primary"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-wt-text-primary">Integration Status</h1>
            <p className="text-wt-text-secondary">
              Monitor integrations across {companies.length} companies
            </p>
          </div>
        </div>
        <Button
          onClick={fetchData}
          className="bg-wt-gold hover:bg-wt-gold-dark text-black"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh All
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-blue-500/10 rounded-lg">
              <CheckCircle className="h-6 w-6 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.configured}</p>
              <p className="text-sm text-wt-text-secondary">Configured</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-green-500/10 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.connected}</p>
              <p className="text-sm text-wt-text-secondary">Connected</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-wt-surface border-wt-border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 bg-orange-500/10 rounded-lg">
              <AlertCircle className="h-6 w-6 text-orange-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-wt-text-primary">{stats.errors}</p>
              <p className="text-sm text-wt-text-secondary">Errors</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Companies Integration Status */}
      <div className="space-y-4">
        {companies.map(company => (
          <Card key={company.id} className="bg-wt-surface border-wt-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-wt-text-primary flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-wt-gold/10 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-wt-gold" />
                  </div>
                  <div>
                    <p className="font-medium">{company.name}</p>
                    <p className="text-xs text-wt-text-secondary font-normal">{company.id}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/super-admin/companies/${company.id}/settings`)}
                  className="text-wt-text-secondary hover:text-wt-gold"
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Settings
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {INTEGRATIONS.map(integration => {
                  const status = getIntegrationDetails(company.id, integration.key);
                  const refreshKey = `${company.id}-${integration.key}`;
                  const isRefreshing = refreshing[refreshKey];
                  const Icon = integration.icon;
                  
                  return (
                    <div
                      key={integration.key}
                      className="p-4 rounded-lg bg-wt-bg border border-wt-border"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Icon className={`h-5 w-5 text-${integration.color}-400`} />
                          <span className="font-medium text-wt-text-primary text-sm">
                            {integration.name}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => refreshIntegration(company.id, integration.key)}
                          disabled={isRefreshing}
                          className="h-6 w-6 text-wt-text-secondary hover:text-wt-gold"
                        >
                          <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>
                      {getStatusBadge(status)}
                      {status?.error && (
                        <p className="mt-2 text-xs text-red-400 truncate" title={status.error}>
                          {status.error}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SuperAdminIntegrationsPage;
